/**
 * Framed Map Upsell
 *
 * Handles the click on the cart-side "Upgrade to a framed map" upsell button.
 * On click:
 *  1. Marks the upsell as "used" so it disappears for the rest of the visit
 *     (frequency: session | browser | cart)
 *  2. Removes the unframed line item the upsell was anchored to (using its line item key,
 *     preserving any line item properties)
 *  3. Adds the framed sibling variant with the same quantity, copying line item properties,
 *     tags the new line with `_upsell: true`, and stores `_revert_variant_id` (hidden) so the
 *     customer can revert to the prior unframed variant from the cart line.
 *  4. Optionally applies a Shopify discount code silently (when configured on the block)
 *  5. Refreshes the cart drawer / cart page in place
 *
 * Revert (".js-framed-upsell-revert"):
 *  - Adds the saved unframed variant with cleaned properties, removes the framed line, clears
 *    cart discount codes only when no `_upsell` lines remain, then refreshes the cart UI.
 *
 * Notes about "once per visitor":
 *   The check happens client-side. Liquid renders an upsell bar on each qualifying
 *   unframed line in the cart; this script then hides all bars if storage says it's
 *   already been used.
 */
(function () {
  'use strict';

  // NOTE: "once per visitor" frequency capping is disabled for now and will be
  // re-enabled later. The Liquid snippet will naturally stop rendering the
  // upsell once the unframed line has been swapped, so for testing this is fine.
  function markUsed() { /* no-op */ }
  function clearUsed() { /* no-op */ }
  function hideAllUpsells() {
    document.querySelectorAll('[data-framed-map-upsell]').forEach(function (el) {
      el.style.display = 'none';
    });
  }
  function applyVisibility() {
    document.querySelectorAll('[data-framed-map-upsell]').forEach(function (el) {
      el.style.display = '';
    });
  }

  function refreshCart() {
    return fetch(window.location.pathname + '?refresh-cart=1', {
      headers: { Accept: 'text/html' },
    })
      .then(function (res) { return res.text(); })
      .then(function (html) {
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, 'text/html');

        var newDrawer = doc.querySelector('cart-drawer');
        var currentDrawer = document.querySelector('cart-drawer');
        if (newDrawer && currentDrawer) {
          currentDrawer.replaceWith(newDrawer);
          if (typeof newDrawer.connectedCallback === 'function') newDrawer.connectedCallback();
          if (typeof newDrawer.open === 'function') newDrawer.open();
        }

        var newCartForm = doc.querySelector('main cart-form, #MainContent cart-form, .container cart-form');
        var currentCartForm = document.querySelector('main cart-form, #MainContent cart-form, .container cart-form');
        if (newCartForm && currentCartForm && currentCartForm !== currentDrawer) {
          currentCartForm.replaceWith(newCartForm);
        }

        applyVisibility();
      })
      .catch(function (err) {
        console.error('Framed upsell: failed to refresh cart', err);
        window.location.reload();
      });
  }

  function setBusy(button, busy) {
    if (!button) return;
    button.disabled = !!busy;
    button.classList.toggle('is-loading', !!busy);
  }

  function addFramedVariant(variantId, quantity, properties, revertVariantId) {
    var body = {
      items: [{ id: parseInt(variantId, 10), quantity: quantity }],
    };
    var props = Object.assign({}, properties || {});
    props._upsell = 'true';
    if (revertVariantId) {
      props._revert_variant_id = String(revertVariantId);
    }
    body.items[0].properties = props;

    return fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    }).then(function (res) {
      if (!res.ok) {
        return res.json().then(function (j) {
          throw new Error((j && j.description) || 'Could not add framed variant');
        });
      }
      return res.json();
    });
  }

  function removeUnframedLine(lineKey) {
    var updates = {};
    updates[lineKey] = 0;
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ updates: updates }),
    }).then(function (res) {
      if (!res.ok) throw new Error('Could not remove unframed line');
      return res.json();
    });
  }

  // Apply a Shopify discount code to the current cart silently.
  //
  // We tried `fetch('/discount/CODE')` first, but Shopify's /discount/<code>
  // endpoint is designed for *page navigations* — over fetch the discount
  // cookie often doesn't propagate before our subsequent /cart re-render runs,
  // so the badge fails to show. The bullet-proof pattern (used by Impulse,
  // Symmetry, etc.) is to do a real navigation in a hidden iframe pointed at
  // /discount/CODE?redirect=/cart.js — that forces Shopify to write the cart
  // cookie and we can resolve as soon as the iframe load fires.
  function applyDiscountCode(code) {
    if (!code) return Promise.resolve();
    return new Promise(function (resolve) {
      var settled = false;
      function done() {
        if (settled) return;
        settled = true;
        try { iframe && iframe.parentNode && iframe.parentNode.removeChild(iframe); } catch (e) { /* ignore */ }
        resolve();
      }
      var iframe = document.createElement('iframe');
      iframe.setAttribute('aria-hidden', 'true');
      iframe.setAttribute('tabindex', '-1');
      iframe.style.cssText = 'position:absolute;width:0;height:0;border:0;opacity:0;pointer-events:none;';
      iframe.addEventListener('load', done);
      iframe.addEventListener('error', done);
      // Safety: never block the upsell flow on a slow/failed discount apply.
      setTimeout(done, 4000);
      iframe.src = '/discount/' + encodeURIComponent(code) + '?redirect=/cart.js';
      (document.body || document.documentElement).appendChild(iframe);
    });
  }

  function safeParseJSON(str, fallback) {
    if (!str) return fallback;
    try {
      return JSON.parse(str);
    } catch (e) {
      return fallback;
    }
  }

  function handleClick(button) {
    if (!button || button.disabled) return;

    var lineKey = button.getAttribute('data-line-key');
    var lineQty = parseInt(button.getAttribute('data-line-qty') || '1', 10) || 1;
    var variantId = button.getAttribute('data-variant-id');
    var discountCode = button.getAttribute('data-discount-code') || '';
    var frequency = button.getAttribute('data-frequency') || 'session';
    var lineProps = safeParseJSON(button.getAttribute('data-line-properties'), {});
    var revertVariantId = button.getAttribute('data-revert-variant-id') || '';

    if (!lineKey || !variantId) return;

    setBusy(button, true);
    hideAllUpsells();

    addFramedVariant(variantId, lineQty, lineProps, revertVariantId)
      .then(function () { return removeUnframedLine(lineKey); })
      .then(function () { return applyDiscountCode(discountCode); })
      .then(function () {
        markUsed(frequency);
        return refreshCart();
      })
      .catch(function (err) {
        console.error('Framed upsell error:', err);
        setBusy(button, false);
        applyVisibility();
      });
  }

  // Reference frequency var so the unused-var lint doesn't fire while the
  // once-per-visitor logic is temporarily disabled.
  void 0;

  function normalizeLineProperties(props) {
    if (!props) return {};
    if (Array.isArray(props)) {
      var out = {};
      props.forEach(function (pair) {
        if (pair && pair.name != null) out[String(pair.name)] = pair.value;
      });
      return out;
    }
    if (typeof props === 'object') return props;
    return {};
  }

  function stripLeadingUnderscoreProps(props) {
    var raw = normalizeLineProperties(props);
    var cleaned = {};
    Object.keys(raw).forEach(function (k) {
      if (k.indexOf('_') === 0) return;
      var v = raw[k];
      if (v != null && String(v).length > 0) cleaned[k] = v;
    });
    return cleaned;
  }

  function addUnframedLineItem(variantId, quantity, properties) {
    var item = { id: parseInt(variantId, 10), quantity: quantity };
    var keys = Object.keys(properties || {});
    if (keys.length > 0) item.properties = properties;
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ items: [item] }),
    }).then(function (res) {
      if (!res.ok) {
        return res.json().then(function (j) {
          throw new Error((j && j.description) || 'Could not restore unframed variant');
        });
      }
      return res.json();
    });
  }

  function maybeClearFramedUpsellDiscount() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (res) {
        if (!res.ok) return null;
        return res.json();
      })
      .then(function (cart) {
        if (!cart || !cart.items) return null;
        var hasUpsell = cart.items.some(function (it) {
          var p = normalizeLineProperties(it.properties);
          return String(p._upsell || '') === 'true';
        });
        if (hasUpsell) return cart;
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({ discount: '' }),
        }).then(function (res) {
          if (!res.ok) return cart;
          return res.json();
        });
      });
  }

  function setRevertBusy(button, busy) {
    if (!button) return;
    button.disabled = !!busy;
    button.classList.toggle('is-loading', !!busy);
    button.setAttribute('aria-busy', busy ? 'true' : 'false');
  }

  function handleRevertClick(button) {
    if (!button || button.disabled) return;

    var lineKey = button.getAttribute('data-line-key');
    if (!lineKey) return;

    setRevertBusy(button, true);

    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (res) {
        if (!res.ok) throw new Error('Could not read cart');
        return res.json();
      })
      .then(function (cart) {
        var line = (cart.items || []).find(function (it) {
          return it.key === lineKey;
        });
        if (!line) throw new Error('Line no longer in cart');
        var raw = normalizeLineProperties(line.properties);
        var revertId = raw._revert_variant_id;
        if (revertId == null || String(revertId) === '') {
          throw new Error('Missing revert reference');
        }
        var cleanProps = stripLeadingUnderscoreProps(line.properties);
        var qty = parseInt(line.quantity, 10) || 1;
        return addUnframedLineItem(revertId, qty, cleanProps).then(function () {
          return removeUnframedLine(lineKey);
        });
      })
      .then(function () {
        return maybeClearFramedUpsellDiscount();
      })
      .then(function () {
        return refreshCart();
      })
      .catch(function (err) {
        console.error('Framed upsell revert error:', err);
        setRevertBusy(button, false);
      });
  }

  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest && e.target.closest('.js-framed-upsell-add');
    if (btn) {
      e.preventDefault();
      handleClick(btn);
    }
    var rev = e.target && e.target.closest && e.target.closest('.js-framed-upsell-revert');
    if (rev) {
      e.preventDefault();
      handleRevertClick(rev);
    }
  });

  // Frame style dropdown — keep the button's data-variant-id and visible text
  // in sync with the dropdown selection. Button text is built from the
  // data-text-template attribute (e.g. "Add {frame} – {discount}% off").
  function renderButtonText(btn, frameName) {
    var template = btn.getAttribute('data-text-template') || 'Add {frame}';
    var discount = btn.getAttribute('data-discount-percent') || '';
    var text = template
      .replace(/\{frame\}/g, frameName || '')
      .replace(/\{discount\}/g, discount);
    var textEl = btn.querySelector('.framed-map-upsell-bar__btn-text');
    if (textEl) textEl.textContent = text;
  }

  document.addEventListener('change', function (e) {
    var sel = e.target && e.target.closest && e.target.closest('.js-framed-upsell-select');
    if (!sel) return;
    var bar = sel.closest('[data-framed-map-upsell]');
    if (!bar) return;
    var btn = bar.querySelector('.js-framed-upsell-add');
    if (!btn) return;

    btn.setAttribute('data-variant-id', sel.value);

    var chosenOpt = sel.options[sel.selectedIndex];
    var chosenLabel = chosenOpt ? (chosenOpt.text || '').trim() : '';
    if (chosenLabel) {
      btn.setAttribute('data-framed-frame-value', chosenLabel);
      renderButtonText(btn, chosenLabel);
    }
  });

  function checkCartEmpty() {
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (cart) {
        if (cart && cart.item_count === 0) {
          clearUsed();
        }
      })
      .catch(function () { /* ignore */ });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      applyVisibility();
      checkCartEmpty();
    });
  } else {
    applyVisibility();
    checkCartEmpty();
  }

  document.addEventListener('on:cart:after-merge', applyVisibility);
  document.addEventListener('dispatch:cart-drawer:refresh', applyVisibility);

  window.FramedMapUpsell = {
    reset: clearUsed,
    refresh: applyVisibility,
  };
})();
